import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import notificationApi from '../../api/notifications';
import { mediaUrl } from '../../api/client';
import { getPushState, subscribeToPush, syncPushSubscription, unsubscribeFromPush } from '../../notifications/push';
import { useParivar } from '../../context/ParivarContext';

import './NotifyBell.scss';

// Set once per device after the first automatic "notifications on by default"
// attempt, so we never nag on every visit.
const AUTORUN_KEY = 'av_push_autotried';

const READ_KEY = 'av_read_notifs';

const loadReadIds = () => {
  try {
    const raw = localStorage.getItem(READ_KEY);
    return new Set(Array.isArray(raw) ? [] : (raw ? JSON.parse(raw) : []));
  } catch (err) {
    return new Set();
  }
};

const persistReadIds = (ids) => {
  try {
    localStorage.setItem(READ_KEY, JSON.stringify([...ids]));
  } catch (err) {
    // Storage may be unavailable (private mode) — unread tracking just resets.
  }
};

// Two bell instances may be mounted (desktop in-menu + mobile top bar).
// Serialise the "notifications on by default" attempt so only the first
// instance ever asks the browser for permission.
let autorunPromise = null;
const ensureAutorun = (parivarId) => {
  if (!autorunPromise) {
    autorunPromise = (async () => {
      const st = await getPushState();
      if (!st.supported || !st.enabled || st.subscribed || st.permission !== 'default') return null;
      if (localStorage.getItem(AUTORUN_KEY)) return null;
      try {
        return await subscribeToPush(parivarId);
      } catch (err) {
        return null;
      } finally {
        localStorage.setItem(AUTORUN_KEY, '1');
      }
    })();
  }
  return autorunPromise;
};

const CATEGORY_META = {
  general: { label: 'General', icon: 'fa-bell' },
  news: { label: 'News', icon: 'fa-newspaper' },
  event: { label: 'Event', icon: 'fa-calendar-check' },
  festival: { label: 'Festival', icon: 'fa-star' },
  live: { label: 'Live', icon: 'fa-video' },
  knowledge: { label: 'Knowledge', icon: 'fa-book-open' },
};

const timeAgo = (iso) => {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(t).toLocaleDateString();
};

function NotifRow({ notif, read, onOpen }) {
  const [imgFailed, setImgFailed] = useState(false);
  const meta = CATEGORY_META[notif.category] || CATEGORY_META.general;
  const src = notif.image_url ? mediaUrl(notif.image_url) : null;
  const showImg = Boolean(src) && !imgFailed;

  return (
    <button
      type="button"
      className={`notify-item${read ? ' notify-item--read' : ' notify-item--unread'}`}
      onClick={() => onOpen(notif)}
    >
      <span className={`notify-thumb cat-${notif.category || 'general'}`}>
        {showImg ? (
          <img src={src} alt="" loading="lazy" onError={() => setImgFailed(true)} />
        ) : (
          <i className={`fas ${meta.icon}`} aria-hidden="true" />
        )}
      </span>
      <span className="notify-item-body">
        <span className="notify-item-title">{notif.title}</span>
        {notif.body && <span className="notify-item-text">{notif.body}</span>}
        <span className="notify-item-meta">
          <span className={`notify-chip chip-${notif.category || 'general'}`}>{meta.label}</span>
          <span className="notify-time">{timeAgo(notif.created_at || notif.sent_at)}</span>
        </span>
      </span>
    </button>
  );
}

/**
 * Notification bell + premium notification centre.
 *
 * Clicking the bell opens a drop-down panel listing recent site notifications
 * (scrollable, with a Load more button). The panel also carries the push
 * on/off switch. Notifications are switched ON by default: the first visit
 * asks for permission once and subscribes automatically for users who accept.
 */
function NotifyBell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedParivar } = useParivar();
  const parivarId = selectedParivar?.id;

  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [justMarked, setJustMarked] = useState(false);
  const wrapRef = useRef(null);
  const nextPageRef = useRef(1);
  const loadedRef = useRef(false);
  const mountedRef = useRef(true);
  const prevParivarIdRef = useRef(undefined);
  const fetchTokenRef = useRef(0);
  const [readIds, setReadIds] = useState(loadReadIds);
  // Server truth for the whole visible feed; null until the first response.
  const [serverUnread, setServerUnread] = useState(null);

  // Initial push state + "notifications on by default" (one quiet attempt).
  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;
    getPushState()
      .then(async (st) => {
        if (cancelled) return;
        setState(st);
        const next = await ensureAutorun(parivarId);
        if (!cancelled && next) setState(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Preload the first page on mount so the header badge shows the unread
  // count immediately. Visitors (no Parivar resolved yet) get the general
  // feed; a staff/known-Parivar load is covered by the scoped refetch below.
  useEffect(() => {
    if (loadedRef.current || parivarId != null) return undefined;
    loadFirstPage();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch the feed whenever the selected Parivar changes (including when
  // it first resolves after mount) so the bell only shows notifications for
  // that Parivar (plus ALL broadcasts). Latest-request-wins avoids a stale
  // unscoped response overwriting the scoped feed.
  useEffect(() => {
    const prev = prevParivarIdRef.current;
    prevParivarIdRef.current = parivarId;
    if (prev === parivarId) return;
    const token = ++fetchTokenRef.current;
    setNotifs([]);
    setHasMore(false);
    setServerUnread(null);
    loadedRef.current = false;
    loadFirstPage(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parivarId]);

  // Re-associate this device's push subscription with the newly-selected
  // Parivar so PARIVAR-targeted alerts (sent by that Parivar's Admin) reach
  // this browser. The backend upserts on endpoint; a Parivar-less endpoint
  // merely gets ALL broadcasts.
  useEffect(() => {
    if (parivarId == null || !(state && state.subscribed)) return undefined;
    syncPushSubscription(parivarId).catch(() => {
      // Best-effort: the feed still works; push scoping may lag one change.
    });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parivarId, state && state.subscribed]);

  // Refresh state when the tab regains focus (permission may change in settings).
  useEffect(() => {
    const onFocus = () => {
      getPushState()
        .then((st) => {
          if (mountedRef.current) setState(st);
        })
        .catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Close the panel when navigating away.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Close the panel when the mobile hamburger menu is opened.
  useEffect(() => {
    const closePanel = () => setOpen(false);
    window.addEventListener('av:close-notifications', closePanel);
    return () => window.removeEventListener('av:close-notifications', closePanel);
  }, []);

  const loadFirstPage = useCallback(async (token) => {
    loadedRef.current = true;
    setLoading(true);
    setError('');
    try {
      const data = await notificationApi.listNotifications({ page: 1, parivar_id: parivarId });
      if (!mountedRef.current) return;
      // A newer Parivar change superseded this request — drop the stale reply.
      if (token != null && token !== fetchTokenRef.current) return;
      setNotifs(data.results || []);
      setHasMore(Boolean(data.next));
      nextPageRef.current = 2;
      // The server's count covers the whole visible feed, not just this page.
      if (typeof data.unread_count === 'number') setServerUnread(data.unread_count);
    } catch (err) {
      if (mountedRef.current && (token == null || token === fetchTokenRef.current)) {
        setError('Could not load notifications right now.');
      }
    } finally {
      if (mountedRef.current && (token == null || token === fetchTokenRef.current)) {
        setLoading(false);
      }
    }
  }, [parivarId]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const data = await notificationApi.listNotifications({
        page: nextPageRef.current,
        parivar_id: parivarId,
      });
      if (!mountedRef.current) return;
      setNotifs((prev) => [...prev, ...(data.results || [])]);
      setHasMore(Boolean(data.next));
      nextPageRef.current += 1;
      if (typeof data.unread_count === 'number') setServerUnread(data.unread_count);
    } catch (err) {
      if (mountedRef.current) setError('Could not load more notifications.');
    } finally {
      if (mountedRef.current) setLoadingMore(false);
    }
  }, [hasMore, loadingMore, parivarId]);

  const togglePanel = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!open && !loadedRef.current) {
      loadedRef.current = true;
      loadFirstPage();
    }
    setOpen((o) => !o);
  };

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const isRead = useCallback(
    (n) => Boolean(n.is_read) || readIds.has(n.id),
    [readIds]
  );

  const openNotification = (notif) => {
    markRead(notif.id);
    setOpen(false);
    if (notif.url) navigate(notif.url);
  };

  const markRead = useCallback((id) => {
    // Local first so the row dims instantly, then persist for other tabs.
    setReadIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      persistReadIds(next);
      return next;
    });
    notificationApi.markRead([id]).catch(() => {
      // Offline or throttled: the local copy still holds for this visit and
      // the server is re-synced the next time the feed loads.
    });
  }, []);

  /**
   * Marks everything as read, not just the pages already in the list.
   *
   * Read state lives on the server, so this is a single request covering the
   * whole visible feed rather than paging through it. The local set is still
   * updated so rows dim immediately and stay right if the call fails.
   */
  const markAllRead = useCallback(async () => {
    if (markingAll) return;
    setMarkingAll(true);
    setError('');
    try {
      await notificationApi.markAllRead(parivarId);
      if (mountedRef.current) {
        setServerUnread(0);
        setJustMarked(true);
      }
    } catch (err) {
      if (mountedRef.current) setError('Could not mark notifications as read.');
    } finally {
      // Optimistic: the rows are marked locally either way, and the next feed
      // load re-syncs from the server.
      setReadIds((prev) => {
        const next = new Set(prev);
        notifs.forEach((n) => next.add(n.id));
        persistReadIds(next);
        return next;
      });
      if (mountedRef.current) setMarkingAll(false);
    }
  }, [markingAll, notifs, parivarId]);

  // Clear the confirmation on its own so the button settles back to idle.
  useEffect(() => {
    if (!justMarked) return undefined;
    const t = window.setTimeout(() => setJustMarked(false), 1800);
    return () => window.clearTimeout(t);
  }, [justMarked]);

  const toggleSubscription = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const next = state && state.subscribed
        ? await unsubscribeFromPush()
        : await subscribeToPush(parivarId);
      if (mountedRef.current) setState(next);
    } catch (err) {
      if (mountedRef.current) setError(err?.message || 'Could not change notification settings.');
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const supported = Boolean(state && state.supported);
  const enabled = Boolean(state && state.enabled);
  const subscribed = Boolean(state && state.subscribed);
  const denied = state && state.permission === 'denied';
  // The server count knows about pages the user has not opened yet, so prefer
  // it and fall back to counting what is on screen.
  const localUnread = notifs.reduce((acc, n) => (isRead(n) ? acc : acc + 1), 0);
  const unreadCount = serverUnread == null ? localUnread : Math.max(serverUnread, localUnread);

  return (
    <div className="notify-bell-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`notify-bell${subscribed ? ' subscribed' : ''}${denied ? ' denied' : ''}${open ? ' active' : ''}`}
        onClick={togglePanel}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label="Notifications"
        aria-haspopup="true"
        aria-expanded={open}
        title={subscribed ? 'Notifications are on' : 'Open notifications'}
      >
        <i className={`fas ${subscribed ? 'fa-bell' : denied ? 'fa-bell-slash' : 'fa-bell'}`} aria-hidden="true" />
        {unreadCount > 0 && !open && (
          <span className="notify-bell-count">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && <div className="notify-panel-backdrop" onClick={() => setOpen(false)} />}

      {open && (
        <div className="notify-panel" role="dialog" aria-label="Notifications">
          <div className="notify-panel-head">
            <span className="notify-panel-title">
              <i className="fas fa-bell" aria-hidden="true" />
              <span>Notifications</span>
            </span>
            <span className="notify-panel-actions">
              <button
                type="button"
                className={`notify-mark-all${justMarked ? ' is-done' : ''}`}
                onClick={markAllRead}
                disabled={unreadCount === 0 || markingAll}
                title={unreadCount > 0 ? `Mark all ${unreadCount} unread notification${unreadCount === 1 ? '' : 's'} as read` : 'No unread notifications'}
              >
                {markingAll ? (
                  <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                ) : (
                  <i className="fas fa-check-double" aria-hidden="true" />
                )}
                <span>
                  {markingAll ? 'Reading…' : justMarked ? 'All read' : 'Mark all read'}
                </span>
              </button>
              <label className="notify-switch" title={subscribed ? 'Turn off notifications' : 'Turn on notifications'}>
                <input
                  type="checkbox"
                  checked={subscribed}
                  disabled={(!supported || !enabled) && !subscribed}
                  onChange={toggleSubscription}
                />
                <span className="notify-switch-track">
                  <span className="notify-switch-thumb" />
                </span>
                <span className="notify-switch-label">{subscribed ? 'On' : 'Off'}</span>
              </label>
            </span>
          </div>

          {error && <p className="notify-panel-error">{error}</p>}

          <div className="notify-panel-list">
            {!loading && notifs.length === 0 && !error && (
              <div className="notify-empty">
                <span className="notify-empty-icon">
                  <i className="fas fa-bell-slash" aria-hidden="true" />
                </span>
                <p>No notifications yet</p>
                <small>New updates about darshan, news and festivals will appear here.</small>
              </div>
            )}
            {loading && (
              <div className="notify-empty">
                <i className="fas fa-spinner fa-spin notify-spin" aria-hidden="true" />
                <p>Loading notifications…</p>
              </div>
            )}
            {notifs.map((n) => (
              <NotifRow key={n.id} notif={n} read={isRead(n)} onOpen={openNotification} />
            ))}
          </div>

          {hasMore && (
            <div className="notify-panel-foot">
              <button type="button" className="notify-load-more" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? (
                  <>
                    <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Loading…
                  </>
                ) : (
                  'Load more'
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotifyBell;